import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import seaTrialsImg from '../../images/events/awards/sea-trials.jpg';
import engineeringImg from '../../images/events/awards/engineering.jpg';
import activeCitizenshipImg from '../../images/events/awards/active-citizenship.jpg';

gsap.registerPlugin(ScrollTrigger);

const awards = [
  {
    id: 'sea-trials',
    title: 'Overall Sea Trials',
    event: 'Portugal Boat Challenge 2025',
    location: 'Barragem da Aguieira, Portugal',
    image: seaTrialsImg,
    description: 'Awarded for achieving the best overall performance across all sea trial (resistance, velocity and slalom), demonstrating excellence in vessel reliability, energy efficiency and navigational precision.',
  },
  {
    id: 'engineering',
    title: 'Engineering Award',
    event: 'Portugal Boat Challenge 2025',
    location: 'Barragem da Aguieira, Portugal',
    image: engineeringImg,
    description: 'Recognized for outstanding engineering innovation, particularly in the integration of sustainable materials.',
  },
  {
    id: 'active-citizenship',
    title: 'Active Citizenship Award',
    event: 'University of Porto',
    location: 'Porto, Portugal',
    image: activeCitizenshipImg,
    description: 'Presented by the University of Porto in 2025 to recognize João Alegre\'s commitment as our team\'s former Team Leader to civic engagement, community outreach, and promoting active citizenship through maritime innovation.',
  },
];

export default function Awards() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const awardsRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (sectionRef.current) {
      gsap.fromTo('.awards-header',
        { y: 60, opacity: 0 },
        { y: 0, opacity: 1, duration: 1, ease: 'power3.out' }
      );
    }
  }, []);

  useEffect(() => {
    if (awardsRef.current) {
      const cards = awardsRef.current.querySelectorAll('.award-card');

      cards.forEach((card, index) => {
        const image = card.querySelector('.award-image');
        const content = card.querySelector('.award-content');
        const line = card.querySelector('.award-line');

        ScrollTrigger.create({
          trigger: card,
          start: 'top 80%',
          end: 'bottom 20%',
          onEnter: () => {
            gsap.fromTo(image,
              { scale: 1.3, opacity: 0 },
              { scale: 1, opacity: 1, duration: 1.2, ease: 'power3.out' }
            );
            gsap.fromTo(content,
              { x: index % 2 === 0 ? -40 : 40, opacity: 0 },
              { x: 0, opacity: 1, duration: 0.8, delay: 0.3, ease: 'power3.out' }
            );
            gsap.fromTo(line,
              { width: 0 },
              { width: '100%', duration: 0.8, delay: 0.5, ease: 'power3.out' }
            );
            setVisibleCount(prev => prev + 1);
          },
          once: true,
        });
      });
    }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full bg-slate-950 py-20 md:py-32">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-mainBlue/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-mainBlue/5 rounded-full blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
        <div ref={headerRef} className="awards-header text-center mb-16 md:mb-24">
          <h2 className="font-custom font-bold text-white md:text-8xl text-5xl">AWARDS</h2>
          <div className="award-line h-1 bg-mainBlue mx-auto mb-6" style={{ width: '0%' }} />
        </div>

        <div ref={awardsRef} className="space-y-16 md:space-y-24">
          {awards.map((award, index) => (
            <div
              key={award.id}
              className={`award-card flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-12 lg:gap-16 items-center opacity-0`}
              style={{ opacity: visibleCount > index ? 1 : 0 }}
            >
              <div className="w-full lg:w-1/2 relative overflow-hidden rounded-2xl group">
                <div className="award-image overflow-hidden">
                  <img
                    src={award.image}
                    alt={award.title}
                    className="w-full h-[250px] md:h-[350px] lg:h-[400px] object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              <div className="w-full lg:w-1/2 award-content">
                <div className="relative">
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-[2px] bg-mainBlue" />
                      <p className="text-mainBlue font-bold text-[10px] md:text-xs uppercase tracking-widest font-custom">
                        {award.event}
                      </p>
                    </div>
                    <h3 className="font-custom font-bold text-2xl md:text-3xl lg:text-4xl text-white mb-3 leading-tight">
                      {award.title}
                    </h3>
                  </div>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed font-custom">
                    {award.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
